import { ChangeEvent, useState } from 'react';
import { Button, TERTIARY, SMALL } from '@glu/buttons-react';
import { Input } from '@glu/form-components';
import locale from '@glu/locale';
import classnames from 'classnames';
import { CheckmarkCircleAshIcon } from '@glu/icons-react';
import { ISavedViewProps } from '../SavedViews/SavedViews';
import useStyles from './SaveOrUpdateView.styles';

export interface ISaveOrUpdateViewProps {
  wasJustSaved?: ISavedViewProps['wasJustSaved'];
  canCreateNew: boolean;
  canSave: boolean;
  onCreate: ISavedViewProps['onCreateSavedView'];
  onSave(): void;
  suggestedName?: string;
}

const SaveOrUpdateView = ({
  canCreateNew,
  canSave,
  onCreate,
  onSave,
  suggestedName = '',
  wasJustSaved,
}: ISaveOrUpdateViewProps) => {
  const classes = useStyles();
  const [name, setName] = useState(suggestedName);
  const [isCreating, setIsCreating] = useState(false);
  if (wasJustSaved) {
    return (
      <div className={classnames(classes.root, classes.rootBorder)}>
        <CheckmarkCircleAshIcon className={classes.check} />
        <span className={classes.saveSuccess}>{locale.get('dataComponents.newViewSaved')}</span>
      </div>
    );
  }
  if (isCreating) {
    return (
      <form
        className={classes.root}
        onSubmit={(e) => {
          e.preventDefault();
          if (name) {
            onCreate({ name });
            setIsCreating(false);
          }
        }}
      >
        <Input
          className={classes.input}
          disableInputAPI
          labelText={locale.get('dataComponents.viewName')}
          name="viewName"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setName(e.target.value);
          }}
          screenReaderLabel
          value={name}
        />
        <Button disabled={!name} size={SMALL} type="submit" variant={TERTIARY}>
          {locale.get('dataComponents.save')}
        </Button>
        <Button
          className={classes.secondButton}
          onClick={() => setIsCreating(false)}
          size={SMALL}
          variant={TERTIARY}
        >
          {locale.get('dataComponents.cancel')}
        </Button>
      </form>
    );
  }
  return (
    <div className={classnames(classes.root, classes.rootBorder)}>
      <span className={classes.changed}>{locale.get('dataComponents.changed')}</span>
      {canSave && (
        <Button onClick={onSave} size={SMALL} variant={TERTIARY}>
          {locale.get('dataComponents.save')}
        </Button>
      )}
      {canCreateNew && (
        <Button
          className={classes.secondButton}
          onClick={() => setIsCreating(true)}
          size={SMALL}
          variant={TERTIARY}
        >
          {locale.get('dataComponents.saveAs')}
        </Button>
      )}
    </div>
  );
};

export default SaveOrUpdateView;
